export const AMHS_STREAM_WS_DUMP_FILENAME = 'amhs-stream-ws-messages.html';

export type AmhsStreamDumpRecord = {
  seq: number;
  receivedAt: string;
  type: string;
  frame: unknown;
};

export type AmhsStreamDumpMeta = {
  fabId?: string | null;
  streamId?: string | null;
  startedAt?: string | null;
  stoppedAt?: string | null;
};

function uniqueTypes(records: AmhsStreamDumpRecord[]): string[] {
  const seen = new Set<string>();
  const types: string[] = [];
  for (const record of records) {
    if (seen.has(record.type)) continue;
    seen.add(record.type);
    types.push(record.type);
  }
  return types;
}

/** Keep `</script>` in frame JSON from terminating the dump page. */
export function serializeDumpPayload(payload: unknown): string {
  return JSON.stringify(payload).replace(/</g, '\\u003c');
}

export function buildAmhsStreamMessageDumpHtml(
  records: AmhsStreamDumpRecord[],
  meta: AmhsStreamDumpMeta = {}
): string {
  const types = uniqueTypes(records);
  const counts: Record<string, number> = {};
  for (const record of records) {
    counts[record.type] = (counts[record.type] ?? 0) + 1;
  }
  const payload = serializeDumpPayload({
    meta: {
      ...meta,
      total: records.length,
      types,
      counts,
    },
    records,
  });

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>AMHS stream WebSocket dump</title>
  <style>
    :root { color-scheme: dark; }
    body {
      margin: 0;
      font-family: ui-sans-serif, system-ui, sans-serif;
      background: #0f1419;
      color: #e7ecf3;
    }
    header {
      padding: 16px 20px 8px;
      border-bottom: 1px solid #243040;
    }
    h1 { margin: 0 0 8px; font-size: 18px; }
    .meta { color: #9aa8b8; font-size: 13px; }
    .tabs {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      padding: 12px 20px;
      border-bottom: 1px solid #243040;
      position: sticky;
      top: 0;
      background: #0f1419;
    }
    button.tab {
      border: 1px solid #314155;
      background: #17202b;
      color: #e7ecf3;
      border-radius: 999px;
      padding: 6px 12px;
      cursor: pointer;
      font-size: 13px;
    }
    button.tab.active { background: #2a6df5; border-color: #2a6df5; }
    .toolbar {
      display: flex;
      gap: 12px;
      align-items: center;
      padding: 10px 20px;
      color: #9aa8b8;
      font-size: 13px;
    }
    .toolbar button {
      border: 1px solid #314155;
      background: #17202b;
      color: #e7ecf3;
      border-radius: 6px;
      padding: 4px 10px;
      cursor: pointer;
    }
    .list { padding: 0 20px 40px; }
    article {
      border: 1px solid #243040;
      border-radius: 8px;
      margin: 0 0 10px;
      overflow: hidden;
    }
    article header {
      padding: 8px 12px;
      border-bottom: 1px solid #243040;
      font-size: 12px;
      color: #9aa8b8;
    }
    pre {
      margin: 0;
      padding: 12px;
      overflow: auto;
      font-size: 12px;
      line-height: 1.45;
      background: #121a24;
    }
  </style>
</head>
<body>
  <header>
    <h1>AMHS stream WebSocket messages</h1>
    <div class="meta" id="meta"></div>
  </header>
  <div class="tabs" id="tabs"></div>
  <div class="toolbar" id="toolbar"></div>
  <div class="list" id="list"></div>
  <script id="dump-data" type="application/json">${payload}</script>
  <script>
    const PAGE_SIZE = 50;
    const dump = JSON.parse(document.getElementById('dump-data').textContent);
    const records = dump.records || [];
    const types = dump.meta.types || [];
    const counts = dump.meta.counts || {};
    let activeType = 'all';
    let page = 0;

    function filtered() {
      return activeType === 'all' ? records : records.filter((r) => r.type === activeType);
    }

    document.getElementById('meta').textContent = [
      dump.meta.fabId ? 'fab ' + dump.meta.fabId : null,
      dump.meta.streamId ? 'stream ' + dump.meta.streamId : null,
      dump.meta.startedAt ? 'started ' + dump.meta.startedAt : null,
      dump.meta.stoppedAt ? 'stopped ' + dump.meta.stoppedAt : null,
      dump.meta.total + ' messages',
      types.length + ' types'
    ].filter(Boolean).join(' · ');

    const tabs = document.getElementById('tabs');
    function renderTabs() {
      tabs.innerHTML = '';
      const items = [{ id: 'all', label: 'All (' + records.length + ')' }].concat(
        types.map((t) => ({ id: t, label: t + ' (' + (counts[t] || 0) + ')' }))
      );
      for (const item of items) {
        const button = document.createElement('button');
        button.className = 'tab' + (item.id === activeType ? ' active' : '');
        button.textContent = item.label;
        button.addEventListener('click', () => {
          activeType = item.id;
          page = 0;
          render();
        });
        tabs.appendChild(button);
      }
    }

    function render() {
      renderTabs();
      const rows = filtered();
      const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
      if (page >= pages) page = pages - 1;
      const start = page * PAGE_SIZE;
      const slice = rows.slice(start, start + PAGE_SIZE);
      const toolbar = document.getElementById('toolbar');
      toolbar.innerHTML = '';
      const summary = document.createElement('span');
      summary.textContent = rows.length
        ? 'Showing ' + (start + 1) + '–' + (start + slice.length) + ' of ' + rows.length
        : 'No messages';
      toolbar.appendChild(summary);
      const prev = document.createElement('button');
      prev.textContent = 'Previous';
      prev.disabled = page <= 0;
      prev.addEventListener('click', () => { page -= 1; render(); });
      const next = document.createElement('button');
      next.textContent = 'Next';
      next.disabled = page >= pages - 1;
      next.addEventListener('click', () => { page += 1; render(); });
      toolbar.appendChild(prev);
      toolbar.appendChild(next);

      const list = document.getElementById('list');
      list.innerHTML = '';
      for (const row of slice) {
        const article = document.createElement('article');
        const header = document.createElement('header');
        header.textContent = '#' + row.seq + ' · ' + row.type + ' · ' + row.receivedAt;
        const pre = document.createElement('pre');
        pre.textContent = JSON.stringify(row.frame, null, 2);
        article.appendChild(header);
        article.appendChild(pre);
        list.appendChild(article);
      }
    }

    render();
  </script>
</body>
</html>
`;
}

export function downloadAmhsStreamMessageDump(
  records: AmhsStreamDumpRecord[],
  meta: AmhsStreamDumpMeta = {}
): void {
  if (records.length === 0 || typeof document === 'undefined') return;
  const html = buildAmhsStreamMessageDumpHtml(records, meta);
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  // Same name every stop so the dump is replaced instead of accumulating files.
  link.download = AMHS_STREAM_WS_DUMP_FILENAME;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
